import React from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Grid, Checkbox, FormControlLabel, CircularProgress } from '@mui/material';
import { TextField, Autocomplete, Button } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { Prompt } from 'react-router-dom';
import { withRights } from 'providers/RightsConsumer';

const PartnerRelationsItemComponent = ({ t, item, partnerRelationTypes, index, onPropertyChange, validation, onSave, rights }) => {
  const onRelationTypeChange = onPropertyChange => event => {
    onPropertyChange(event ? event.relationId : '', event ? event.directRelation : '');
  };
  return (
    <Grid container alignContent='center' alignItems='center' spacing={2}>
      <Prompt when={item.changed} message={t('LeavingWithoutSaving')} />
      <Grid item xs={5} sm={4} md={4}>
        <TextField
          fullWidth
          value={item.directPartnerUniqueID || ''}
          error={!isValid(validation.directPartnerUniqueID) || !isValid(validation.directPartnerId)}
          helperText={getErrors(validation.directPartnerUniqueID) || getErrors(validation.directPartnerId)}
          name='directPartnerUniqueId'
          label={t('Offer.PartnerRelations.DirectPartner')}
          onChange={onPropertyChange(index, 'directPartnerUniqueID')}
          endAdornment={item.directPartnerLoading && <CircularProgress size={'1rem'} />}
          disabled={rights.readOnlyFull || !rights.clientRelationsWrite}
        />
      </Grid>
      <Grid item xs={5} sm={4} md={4}>
        <TextField
          fullWidth
          value={item.reversePartnerUniqueID || ''}
          error={!isValid(validation.reversePartnerUniqueID) || !isValid(validation.reversePartnerId)}
          helperText={getErrors(validation.reversePartnerUniqueID) || getErrors(validation.reversePartnerId)}
          name='reversePartnerUniqueId'
          label={t('Offer.PartnerRelations.ReversePartner')}
          onChange={onPropertyChange(index, 'reversePartnerUniqueID')}
          endAdornment={item.reversePartnerLoading && <CircularProgress size={'1rem'} />}
          disabled={rights.readOnlyFull || !rights.clientRelationsWrite}
        />
      </Grid>
      <Grid item xs={5} sm={4} md={4}>
        <Autocomplete
          fullWidth
          name='relationType'
          value={item.relationTypeId}
          valueKey='relationId'
          labelKey='directRelation'
          label={t('Offer.PartnerRelations.DirectRelation')}
          onChange={onRelationTypeChange(onPropertyChange(index, 'relationTypeId', 'directRelation'))}
          options={partnerRelationTypes}
          isClearable={false}
          error={!isValid(validation.relationTypeId)}
          helperText={getErrors(validation.relationTypeId)}
          disabled={rights.readOnlyFull || !rights.clientRelationsWrite}
        />
      </Grid>
      <Grid item xs={5} sm={4} md={4}>
        <Autocomplete
          fullWidth
          name='relationType'
          value={item.relationTypeId}
          valueKey='relationId'
          labelKey='reverseRelation'
          label={t('Offer.PartnerRelations.ReverseRelation')}
          onChange={onPropertyChange(index, 'relationTypeId')}
          options={partnerRelationTypes}
          isClearable={false}
          disabled={rights.readOnlyFull || !rights.clientRelationsWrite}
        />
      </Grid>
      <Grid item xs={5} sm={4} md={4}>
        <DateTime
          name={'relationStartDate'}
          fullWidth
          value={item.relationStartDate}
          label={t('Offer.PartnerRelations.RelationStartDate')}
          onChange={onPropertyChange(index, 'relationStartDate')}
          timeFormat={false}
          error={!isValid(validation.relationStartDate)}
          helperText={getErrors(validation.relationStartDate)}
          disabled={rights.readOnlyFull || !rights.clientRelationsWrite}
        />
      </Grid>
      <Grid item xs={5} sm={4} md={4}>
        <DateTime
          name={'relationExpireDate'}
          fullWidth
          value={item.relationExpireDate}
          label={t('Offer.PartnerRelations.RelationExpireDate')}
          onChange={onPropertyChange(index, 'relationExpireDate')}
          timeFormat={false}
          error={!isValid(validation.relationExpireDate)}
          helperText={getErrors(validation.relationExpireDate).split(/(T)+[0-9]/)[0]}
          disabled={rights.readOnlyFull || !rights.clientRelationsWrite}
        />
      </Grid>
      <Grid item xs={5} sm={4} md={4}>
        <TextField
          fullWidth
          isNumeric
          inputProps={{ decimalScale: 0, thousandSeparator: false }}
          name='capitalPercent'
          value={item.capitalPercent || ''}
          onChange={onPropertyChange(index, 'capitalPercent')}
          label={t('Offer.PartnerRelations.CapitalPercent')}
          disabled={rights.readOnlyFull || !rights.clientRelationsWrite}
        />
      </Grid>
      <Grid item xs={5} sm={4} md={4}>
        <TextField
          fullWidth
          disabled
          name='lastUpdateUserName'
          value={item.lastUpdateUserName || ''}
          label={t('Offer.PartnerRelations.LastUpdateUserName')}
        />
      </Grid>
      <Grid item xs={5} sm={4} md={4}>
        <DateTime
          name={'lastUpdateDate'}
          fullWidth
          value={item.lastUpdateDate}
          label={t('Offer.PartnerRelations.LastUpdateDate')}
          timeFormat={false}
          disabled
        />
      </Grid>
      <Grid item xs={4} sm={2} md={1}>
        <FormControlLabel
          control={
            <Checkbox
              id={'partnerRelations_isActive'}
              checked={item.isActive}
              color='primary'
              onChange={onCheckBoxChange(onPropertyChange(index, 'isActive'))}
              name='isActive'
              disabled={rights.readOnlyFull || !rights.clientRelationsWrite}
            />
          }
          label={t('Offer.PartnerRelations.IsActive')}
        />
      </Grid>
      <Grid item xs={2} sm={1}>
        <Button
          color='primary'
          onClick={() => onSave(index)}
          disabled={rights.readOnlyFull || !rights.clientRelationsWrite || !item.changed || !(validation |> isValid)}
        >
          {item.saving ? t('Saving') + '...' : t('Save')}
        </Button>
      </Grid>
    </Grid>
  );
};

PartnerRelationsItemComponent.propTypes = {
  t: PropTypes.func.isRequired,
  item: PropTypes.object.isRequired,
  partnerRelationTypes: PropTypes.array.isRequired,
  index: PropTypes.number.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  validation: PropTypes.object.isRequired,
  rights: PropTypes.bool.isRequired
};

export default compose(withTranslation())(withRights(PartnerRelationsItemComponent));
